import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import PendingOrderAlertScreen from './PendingOrderAlertScreen';
import React, { useMemo } from 'react';
import { OrderMethod, OrdersPauseReason } from '../../services/exports/Constants';
import useTick from '../../hooks/utility/useTick';
import { useSelector } from 'react-redux';
import OrdersPausedAlertScreen from './OrdersPausedAlertScreen';
import { collect } from 'collect.js';
import useDeepRef from '../../hooks/utility/useDeepRef';
import OrderPreparationReminderScreen from './OrderPreparationReminderScreen';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import OrderPickupTimeUpdatedAlertScreen from './OrderPickupTimeUpdatedAlertScreen';
import { DateTime } from 'luxon';

export default function AlertScreensStack() {
  const { activeOrdersList, pendingOrdersList, scheduledOrdersList } = useOngoingOrders();

  const { company } = useSelector((state) => state.company);
  const ordersWithAssignedDriverList = useMemo(
    () =>
      collect(activeOrdersList)
        .filter(
          (item) =>
            OrderHelpers.isExternalDelivery(item) && item.is_driver_assigned && !item.is_driver_assignment_handled,
        )
        .sortBy((item) => DateTime.fromISO(item.estimated_pickup_time).toUnixInteger())
        .toArray(),
    [activeOrdersList],
  );
  const ordersWithNonHandledPickupTimeChanges = useMemo(
    () =>
      collect(activeOrdersList)
        .filter(
          (item) =>
            OrderHelpers.isExternalDelivery(item) &&
            item.is_driver_assigned &&
            item.is_driver_assignment_handled &&
            !item.is_pickup_time_change_handled,
        )
        .sortBy((item) => DateTime.fromISO(item.estimated_pickup_time).toUnixInteger())
        .toArray(),
    [activeOrdersList],
  );
  const scheduledOrdersListRef = useDeepRef(scheduledOrdersList);
  const { value: scheduledOrdersToStartPreparationList } = useTick(() =>
    collect(scheduledOrdersListRef.current)
      .filter((item) => !OrderHelpers.isExternalDelivery(item) && isScheduledOrderPreparationPointPassed(item))
      .toArray(),
  );
  const { value: showOrdersPausedScreen } = useTick(
    () =>
      company?.orders_paused_from !== null &&
      DateTime.fromISO(company.orders_paused_from) < DateTime.local() &&
      company?.orders_paused_until !== null &&
      DateTime.fromISO(company.orders_paused_until) > DateTime.local() &&
      company?.orders_pause_reason !== OrdersPauseReason.Manual,
    10000,
  );

  const showPendingOrderScreen = !showOrdersPausedScreen && pendingOrdersList.length > 0;
  const showDriverAssignedScreen =
    !showOrdersPausedScreen && !showPendingOrderScreen && ordersWithAssignedDriverList.length > 0;
  const showPickupTimeUpdatedScreen =
    !showOrdersPausedScreen &&
    !showPendingOrderScreen &&
    !showDriverAssignedScreen &&
    ordersWithNonHandledPickupTimeChanges.length > 0;
  const showScheduledOrderReminderScreen =
    !showOrdersPausedScreen &&
    !showPendingOrderScreen &&
    !showDriverAssignedScreen &&
    !showPickupTimeUpdatedScreen &&
    scheduledOrdersToStartPreparationList.length > 0;

  function isScheduledOrderPreparationPointPassed(order) {
    const minutes =
      order.method === OrderMethod.Delivery
        ? company.average_order_delivery_time + company.busy_mode_minutes
        : company.average_order_preparation_time + company.busy_mode_minutes;

    return DateTime.local() >= DateTime.fromISO(order.estimated_pickup_time).minus({ minutes });
  }

  return (
    <>
      {showOrdersPausedScreen && <OrdersPausedAlertScreen />}
      {showPendingOrderScreen && <PendingOrderAlertScreen order={pendingOrdersList[0]} />}
      {showDriverAssignedScreen && <OrderPreparationReminderScreen order={ordersWithAssignedDriverList[0]} />}
      {showPickupTimeUpdatedScreen && (
        <OrderPickupTimeUpdatedAlertScreen order={ordersWithNonHandledPickupTimeChanges[0]} />
      )}
      {showScheduledOrderReminderScreen && (
        <OrderPreparationReminderScreen order={scheduledOrdersToStartPreparationList[0]} />
      )}
    </>
  );
}
